import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import { PageHeader } from "../../components/common/PageHeader";
import { useSnackbar } from "../../components/common/SnackbarProvider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import {
  useListOrgAuditLogs,
  getExportOrgAuditLogsUrl,
  useListMembers,
} from "../../api/generated/generated";
import type {
  AuditLogEntry,
  AuditLogListResponse,
  AuditAction,
  AuditResourceType,
  ExportOrgAuditLogsParams,
  Member,
} from "../../api/generated";
import { exportAuditLogsCsv } from "../../utils/exportCsv";
import { useAuth } from "../../contexts/AuthContext";
import { getErrorMessage } from "../../api/client";

const ACTION_OPTIONS: { value: AuditAction; label: string }[] = [
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
];

const RESOURCE_TYPE_OPTIONS: { value: AuditResourceType; label: string }[] = [
  { value: "organization", label: "Organization" },
  { value: "member", label: "Member" },
  { value: "prompt", label: "Prompt" },
  { value: "conversation", label: "Conversation" },
  { value: "vector_store", label: "Vector Store" },
  { value: "vector_store_file", label: "File" },
  { value: "transcript", label: "Transcript" },
  { value: "integration", label: "Integration" },
];

const ACTION_COLORS: Record<AuditAction, "success" | "info" | "error"> = {
  create: "success",
  update: "info",
  delete: "error",
};

function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max) + "..." : str;
}

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleString();
}

export function OrgAuditLogPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? "";
  const { data: membersResponse } = useListMembers(orgId);
  const members: Member[] | undefined =
    membersResponse?.status === 200 ? membersResponse.data : undefined;

  // Filters
  const [filters, setFilters] = useState<ExportOrgAuditLogsParams>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Detail dialog
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(
    null,
  );

  // Export state
  const [exporting, setExporting] = useState(false);
  const { showSnackbar } = useSnackbar();

  const { data: response, isLoading } = useListOrgAuditLogs({
    ...filters,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  });
  const data = response?.data as AuditLogListResponse | undefined;

  const handleFilterChange = (
    key: keyof ExportOrgAuditLogsParams,
    value: string,
  ) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value) {
        (next as Record<string, string>)[key] = value;
      } else {
        delete next[key];
      }
      return next;
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(0);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportAuditLogsCsv(
        getExportOrgAuditLogsUrl(filters),
        "audit-logs.csv",
      );
      showSnackbar("CSV exported");
    } catch (err) {
      showSnackbar(getErrorMessage(err), "error");
    } finally {
      setExporting(false);
    }
  };

  const hasFilters = Object.keys(filters).length > 0;

  return (
    <Box>
      <PageHeader
        title="Audit Log"
        actions={
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting || !data?.entries.length}
          >
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        }
      />
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            label="From"
            type="date"
            size="small"
            value={filters.start_date ?? ""}
            onChange={(e) => handleFilterChange("start_date", e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={filters.end_date ?? ""}
            onChange={(e) => handleFilterChange("end_date", e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>User</InputLabel>
            <Select
              value={filters.user_id ?? ""}
              label="User"
              onChange={(e) => handleFilterChange("user_id", e.target.value)}
            >
              <MenuItem value="">All Users</MenuItem>
              {members?.map((m: Member) => (
                <MenuItem key={m.uid} value={m.uid}>
                  {m.display_name || m.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={filters.action ?? ""}
              label="Action"
              onChange={(e) => handleFilterChange("action", e.target.value)}
            >
              <MenuItem value="">All Actions</MenuItem>
              {ACTION_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Resource</InputLabel>
            <Select
              value={filters.resource_type ?? ""}
              label="Resource"
              onChange={(e) =>
                handleFilterChange("resource_type", e.target.value)
              }
            >
              <MenuItem value="">All Resources</MenuItem>
              {RESOURCE_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasFilters && (
            <Button size="small" onClick={handleClearFilters}>
              Clear
            </Button>
          )}
        </Box>
      </Paper>
      {/* Table */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Resource ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography
                      sx={{
                        color: "text.secondary",
                        py: 4
                      }}>
                      No audit log entries found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {data?.entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedEntry(entry)}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatTimestamp(entry.timestamp)}
                  </TableCell>
                  <TableCell>{entry.user_email ?? entry.user_id}</TableCell>
                  <TableCell>
                    <Chip
                      label={entry.action}
                      color={ACTION_COLORS[entry.action]}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{entry.resource_type.replace("_", " ")}</TableCell>
                  <TableCell>
                    <Tooltip title={entry.resource_id}>
                      <span>{truncate(entry.resource_id, 16)}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </TableContainer>
      )}
      {/* Detail Dialog */}
      <Dialog
        open={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedEntry && (
          <>
            <DialogTitle>
              Audit Entry — {selectedEntry.action}{" "}
              {selectedEntry.resource_type.replace("_", " ")}
            </DialogTitle>
            <DialogContent dividers>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="body2" sx={{
                  fontWeight: 600
                }}>
                  Timestamp
                </Typography>
                <Typography variant="body2">
                  {formatTimestamp(selectedEntry.timestamp)}
                </Typography>
                <Typography variant="body2" sx={{
                  fontWeight: 600
                }}>
                  User
                </Typography>
                <Typography variant="body2">
                  {selectedEntry.user_email ?? selectedEntry.user_id}
                </Typography>
                <Typography variant="body2" sx={{
                  fontWeight: 600
                }}>
                  Action
                </Typography>
                <Typography variant="body2">{selectedEntry.action}</Typography>
                <Typography variant="body2" sx={{
                  fontWeight: 600
                }}>
                  Resource Type
                </Typography>
                <Typography variant="body2">
                  {selectedEntry.resource_type}
                </Typography>
                <Typography variant="body2" sx={{
                  fontWeight: 600
                }}>
                  Resource ID
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {selectedEntry.resource_id}
                </Typography>
              </Box>

              {selectedEntry.details && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Details
                  </Typography>
                  {selectedEntry.details.before &&
                  selectedEntry.details.after ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{
                          color: "text.secondary"
                        }}>
                          Before
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5 }}>
                          <pre
                            style={{
                              margin: 0,
                              fontSize: 12,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {JSON.stringify(
                              selectedEntry.details.before,
                              null,
                              2,
                            )}
                          </pre>
                        </Paper>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{
                          color: "text.secondary"
                        }}>
                          After
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5 }}>
                          <pre
                            style={{
                              margin: 0,
                              fontSize: 12,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {JSON.stringify(
                              selectedEntry.details.after,
                              null,
                              2,
                            )}
                          </pre>
                        </Paper>
                      </Box>
                    </Box>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <pre
                        style={{
                          margin: 0,
                          fontSize: 12,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {JSON.stringify(selectedEntry.details, null, 2)}
                      </pre>
                    </Paper>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedEntry(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
